"use client"

import { ComparisonTable } from "../../components/comparison-table"
import { KeyInsight } from "../../components/key-insight"

export function KirbeContent() {
  return (
    <article className="prose-container">
      {/* Title Section */}
      <header className="mb-16">
        <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-4">
          Use Case Documentation
        </p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground leading-tight mb-6 text-balance">
          KiRBe
        </h1>
        <p className="text-xl sm:text-2xl text-muted-foreground leading-relaxed max-w-3xl">
          Knowledge-integrated Reasoning &amp; Business Engine
        </p>
        <div className="flex items-center gap-4 mt-8 pt-8 border-t border-border">
          <div className="text-sm text-muted-foreground">
            <span className="font-medium text-foreground">Reading time:</span> 7 min
          </div>
          <div className="w-1 h-1 rounded-full bg-muted-foreground/40" />
          <div className="text-sm text-muted-foreground">
            <span className="font-medium text-foreground">Version:</span> 1.2
          </div>
          <div className="w-1 h-1 rounded-full bg-muted-foreground/40" />
          <div className="text-sm text-muted-foreground">
            <span className="font-medium text-foreground">Last updated:</span> 2026
          </div>
        </div>
      </header>

      {/* Introduction */}
      <section id="overview" className="mb-16 scroll-mt-40">
        <span className="text-sm font-semibold text-accent-primary uppercase tracking-wider">
          01 — Overview
        </span>
        <h2 className="text-3xl md:text-4xl font-bold text-foreground mt-4 mb-6">
          Enterprise Knowledge Intelligence
        </h2>
        <p className="text-lg text-muted-foreground leading-relaxed mb-6">
          <strong className="text-foreground">K</strong>nowledge-<strong className="text-foreground">i</strong>ntegrated 
          <strong className="text-foreground"> R</strong>easoning &amp; <strong className="text-foreground">B</strong>usiness 
          <strong className="text-foreground"> e</strong>ngine — KiRBe is an enterprise-grade knowledge management platform 
          designed for organizations seeking fast, accurate internal knowledge retrieval with compliance-ready 
          audit capabilities. Built for teams that need intelligent search without the complexity.
        </p>
        <p className="text-muted-foreground leading-relaxed mb-6">
          KiRBe serves as the Intelligence Layer in the MADEIRA architecture, providing semantic 
          knowledge retrieval that combines lexical and semantic search methodologies 
          using advanced fusion algorithms for significantly improved retrieval quality over single-method approaches.
        </p>
        <KeyInsight>
          KiRBe is the memory and knowledge backbone for MADEIRA agents. When an agent needs 
          to retrieve organizational knowledge, KiRBe provides deterministic, accurate results 
          with full provenance tracking and audit trails.
        </KeyInsight>
      </section>

      {/* Semantic Chunking */}
      <section id="capabilities" className="mb-16 scroll-mt-40">
        <span className="text-sm font-semibold text-accent-primary uppercase tracking-wider">
          02 — Semantic Chunking Strategy
        </span>
        <h2 className="text-3xl md:text-4xl font-bold text-foreground mt-4 mb-6">
          Dynamic Overlapping &amp; Context Preservation
        </h2>
        
        <p className="text-muted-foreground leading-relaxed mb-8">
          At the heart of KiRBe&apos;s retrieval quality is our semantic chunking strategy. Rather than 
          splitting documents at arbitrary boundaries, we employ context-aware segmentation that 
          preserves meaning across chunk boundaries through dynamic overlapping.
        </p>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <div className="p-6 rounded-lg border border-border bg-card/50">
            <h3 className="text-lg font-semibold text-foreground mb-3">Sentence-Aware Windowing</h3>
            <p className="text-muted-foreground text-sm leading-relaxed">
              Documents are segmented at natural linguistic boundaries—sentence and paragraph 
              structures—rather than arbitrary token counts. This preserves semantic coherence 
              and improves downstream retrieval accuracy.
            </p>
          </div>
          <div className="p-6 rounded-lg border border-border bg-card/50">
            <h3 className="text-lg font-semibold text-foreground mb-3">Dynamic Overlap Strategy</h3>
            <p className="text-muted-foreground text-sm leading-relaxed">
              Each chunk maintains contextual overlap with its neighbors, ensuring that 
              cross-boundary concepts remain discoverable. The overlap ratio is calibrated 
              for optimal retrieval without excessive redundancy.
            </p>
          </div>
          <div className="p-6 rounded-lg border border-border bg-card/50">
            <h3 className="text-lg font-semibold text-foreground mb-3">Metadata Enrichment</h3>
            <p className="text-muted-foreground text-sm leading-relaxed">
              Each chunk carries positional metadata—its index within the document, total 
              chunk count, and source anchors—enabling precise provenance tracking and 
              context reconstruction.
            </p>
          </div>
          <div className="p-6 rounded-lg border border-border bg-card/50">
            <h3 className="text-lg font-semibold text-foreground mb-3">Adaptive Processing</h3>
            <p className="text-muted-foreground text-sm leading-relaxed">
              The system intelligently adapts its parsing strategy based on document characteristics, 
              automatically selecting the optimal chunking method for each content type while 
              maintaining consistent retrieval quality across all formats.
            </p>
          </div>
        </div>

        <ComparisonTable 
          title="Chunking Parameters"
          headers={["Parameter", "Configuration", "Purpose"]}
          rows={[
            ["Segment Size", "Optimized for retrieval", "Balances context and specificity"],
            ["Overlap Ratio", "~25% contextual window", "Ensures boundary coherence"],
            ["Boundary Detection", "Linguistic markers", "Preserves semantic units"],
            ["Position Tracking", "Full provenance chain", "Enables context reconstruction"],
          ]}
        />
      </section>

      {/* NLP Compression */}
      <section id="compression" className="mb-16 scroll-mt-40">
        <span className="text-sm font-semibold text-accent-primary uppercase tracking-wider">
          03 — NLP Compression &amp; Postprocessing
        </span>
        <h2 className="text-3xl md:text-4xl font-bold text-foreground mt-4 mb-6">
          Intelligent Information Refinement
        </h2>

        <p className="text-muted-foreground leading-relaxed mb-8">
          Raw retrieval results undergo a sophisticated postprocessing pipeline that combines 
          multiple NLP techniques to surface the most relevant information while filtering noise. 
          This multi-stage refinement ensures precision without sacrificing recall.
        </p>

        <div className="space-y-6 mb-8">
          <div className="border-l-2 border-accent-primary pl-6">
            <h3 className="text-xl font-semibold text-foreground mb-2">Hybrid Fusion Scoring</h3>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Search results from lexical and semantic methods are combined using rank-based 
              fusion algorithms. This approach leverages the strengths of both methods: 
              exact keyword matching for precision and semantic similarity for conceptual relevance.
            </p>
            <div className="flex gap-4 text-sm">
              <span className="text-accent-primary">Dual-method retrieval</span>
              <span className="text-muted-foreground/60">|</span>
              <span className="text-muted-foreground">Configurable weighting</span>
            </div>
          </div>

          <div className="border-l-2 border-amber-500/50 pl-6">
            <h3 className="text-xl font-semibold text-foreground mb-2">Contextual Reranking</h3>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Initial results pass through a neural reranking stage that evaluates query-document 
              relevance at a deeper semantic level. This second-pass refinement significantly 
              improves top-K precision for complex, nuanced queries.
            </p>
            <div className="flex gap-4 text-sm">
              <span className="text-amber-500">Neural relevance scoring</span>
              <span className="text-muted-foreground/60">|</span>
              <span className="text-muted-foreground">Query-aware refinement</span>
            </div>
          </div>

          <div className="border-l-2 border-teal-500/50 pl-6">
            <h3 className="text-xl font-semibold text-foreground mb-2">Entity-Aware Filtering</h3>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Named entity recognition identifies key entities (people, organizations, dates, 
              concepts) within results, enabling entity-based filtering and boosting. Results 
              containing query-relevant entities are prioritized automatically.
            </p>
            <div className="flex gap-4 text-sm">
              <span className="text-teal-500">NER integration</span>
              <span className="text-muted-foreground/60">|</span>
              <span className="text-muted-foreground">Entity-boosted ranking</span>
            </div>
          </div>

          <div className="border-l-2 border-slate-500/50 pl-6">
            <h3 className="text-xl font-semibold text-foreground mb-2">Keyword Extraction &amp; Matching</h3>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Automated keyword extraction identifies salient terms from both queries and documents. 
              This enables precise keyword matching as a complementary signal to semantic similarity, 
              particularly valuable for domain-specific terminology.
            </p>
            <div className="flex gap-4 text-sm">
              <span className="text-slate-400">Term extraction</span>
              <span className="text-muted-foreground/60">|</span>
              <span className="text-muted-foreground">Domain vocabulary support</span>
            </div>
          </div>
        </div>

        <KeyInsight>
          The postprocessing pipeline is designed for extensibility. Custom filters can be 
          injected at any stage, allowing organizations to encode domain-specific relevance 
          criteria without modifying core search logic.
        </KeyInsight>
      </section>

      {/* Architecture */}
      <section id="architecture" className="mb-16 scroll-mt-40">
        <span className="text-sm font-semibold text-accent-primary uppercase tracking-wider">
          04 — Architecture Principles
        </span>
        <h2 className="text-3xl md:text-4xl font-bold text-foreground mt-4 mb-6">
          Security-First Design
        </h2>
        
        <p className="text-muted-foreground leading-relaxed mb-8">
          KiRBe is built on a service-oriented architecture with strict separation of concerns. 
          All data access is mediated through role-based access control with row-level 
          security enforcement. No sensitive implementation details—provider names, schema 
          structures, or internal routes—are exposed to client applications.
        </p>

        <div className="bg-card/50 border border-border rounded-lg p-6 mb-8">
          <h4 className="font-semibold text-foreground mb-4">Core Architectural Tenets</h4>
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <span className="flex-shrink-0 w-8 h-8 rounded-full bg-accent-primary/20 flex items-center justify-center text-xs font-semibold text-accent-primary">01</span>
              <div>
                <p className="font-medium text-foreground">Abstraction Layer</p>
                <p className="text-sm text-muted-foreground">All backend services are accessed through abstracted interfaces. Clients interact with logical operations, never implementation primitives.</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <span className="flex-shrink-0 w-8 h-8 rounded-full bg-accent-primary/20 flex items-center justify-center text-xs font-semibold text-accent-primary">02</span>
              <div>
                <p className="font-medium text-foreground">Tenant Isolation</p>
                <p className="text-sm text-muted-foreground">Each organization&apos;s data is isolated at the infrastructure level. Cross-tenant queries are architecturally impossible.</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <span className="flex-shrink-0 w-8 h-8 rounded-full bg-accent-primary/20 flex items-center justify-center text-xs font-semibold text-accent-primary">03</span>
              <div>
                <p className="font-medium text-foreground">Audit Completeness</p>
                <p className="text-sm text-muted-foreground">Every access event, permission change, and search query is logged with immutable timestamps for compliance reporting.</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <span className="flex-shrink-0 w-8 h-8 rounded-full bg-accent-primary/20 flex items-center justify-center text-xs font-semibold text-accent-primary">04</span>
              <div>
                <p className="font-medium text-foreground">Server-Side Processing</p>
                <p className="text-sm text-muted-foreground">Sensitive operations—embedding generation, search execution, reranking—occur server-side. Clients submit queries, not vectors.</p>
              </div>
            </div>
          </div>
        </div>

        <ComparisonTable 
          title="Enterprise Compliance"
          headers={["Capability", "Implementation", "Standard"]}
          rows={[
            ["Access Control", "Role-based, row-level security", "SOC2 Type II"],
            ["Audit Logging", "Immutable event streams", "GDPR Article 30"],
            ["Data Retention", "Configurable retention policies", "Industry-specific"],
            ["Usage Metering", "Per-tenant quota enforcement", "Fair use governance"],
          ]}
        />
      </section>

      {/* Data Ingestion */}
      <section id="ingestion" className="mb-16 scroll-mt-40">
        <span className="text-sm font-semibold text-accent-primary uppercase tracking-wider">
          05 — Data Ingestion
        </span>
        <h2 className="text-3xl md:text-4xl font-bold text-foreground mt-4 mb-6">
          Multi-Source Connectors
        </h2>
        
        <p className="text-muted-foreground leading-relaxed mb-6">
          KiRBe supports ingestion from a wide variety of sources through a unified connector 
          architecture. Each connector normalizes content into a common document model before 
          the semantic chunking and indexing pipeline.
        </p>

        <div className="grid md:grid-cols-3 gap-6">
          <div className="p-6 rounded-lg border border-border bg-card/50">
            <span className="text-xs font-semibold text-muted-foreground uppercase">Documents</span>
            <h3 className="text-lg font-semibold text-foreground mt-2 mb-3">File Processing</h3>
            <p className="text-muted-foreground text-sm leading-relaxed">
              PDFs, Office documents, Markdown, HTML, and images with OCR. Automatic format 
              detection and intelligent text extraction.
            </p>
          </div>
          <div className="p-6 rounded-lg border border-border bg-card/50">
            <span className="text-xs font-semibold text-muted-foreground uppercase">Cloud</span>
            <h3 className="text-lg font-semibold text-foreground mt-2 mb-3">Platform Connectors</h3>
            <p className="text-muted-foreground text-sm leading-relaxed">
              Sync with cloud storage, documentation platforms, and communication tools. 
              OAuth-based authentication with automatic token refresh.
            </p>
          </div>
          <div className="p-6 rounded-lg border border-border bg-card/50">
            <span className="text-xs font-semibold text-muted-foreground uppercase">Web</span>
            <h3 className="text-lg font-semibold text-foreground mt-2 mb-3">Web Scraping</h3>
            <p className="text-muted-foreground text-sm leading-relaxed">
              Intelligent web page extraction with support for dynamic content. Checksum-based 
              deduplication prevents redundant indexing.
            </p>
          </div>
        </div>
      </section>

      {/* Getting Started */}
      <section id="getting-started" className="scroll-mt-40">
        <span className="text-sm font-semibold text-accent-primary uppercase tracking-wider">
          06 — Getting Started
        </span>
        <h2 className="text-3xl md:text-4xl font-bold text-foreground mt-4 mb-6">
          Guided Onboarding
        </h2>
        
        <p className="text-muted-foreground leading-relaxed mb-6">
          KiRBe includes a streamlined onboarding flow that takes teams from zero to first 
          search in under two minutes. The wizard guides you through organization setup, 
          knowledge vault creation, document ingestion, and team invitation.
        </p>

        <div className="grid md:grid-cols-3 gap-6">
          <div className="p-6 rounded-lg border border-border bg-card/50">
            <span className="text-xs font-semibold text-muted-foreground uppercase">Step 1-2</span>
            <h3 className="text-lg font-semibold text-foreground mt-2 mb-3">Org &amp; Vault</h3>
            <p className="text-muted-foreground text-sm leading-relaxed">
              Create your organization and set up your first knowledge vault. Optional sample 
              documents help you explore search capabilities immediately.
            </p>
          </div>
          <div className="p-6 rounded-lg border border-border bg-card/50">
            <span className="text-xs font-semibold text-muted-foreground uppercase">Step 3-4</span>
            <h3 className="text-lg font-semibold text-foreground mt-2 mb-3">Ingest &amp; Search</h3>
            <p className="text-muted-foreground text-sm leading-relaxed">
              Connect your data sources or upload documents directly. Test search quality 
              with real-time progress tracking and result previews.
            </p>
          </div>
          <div className="p-6 rounded-lg border border-border bg-card/50">
            <span className="text-xs font-semibold text-muted-foreground uppercase">Step 5</span>
            <h3 className="text-lg font-semibold text-foreground mt-2 mb-3">Team Access</h3>
            <p className="text-muted-foreground text-sm leading-relaxed">
              Invite team members with role-based permissions. Full audit logging begins 
              immediately upon team creation.
            </p>
          </div>
        </div>
      </section>
    </article>
  )
}
